// src/Components/ActaViewer.jsx
import React, { useEffect, useState, useLayoutEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { GetPDFs } from "../../Backend/ApiCalls";
import { openExternal } from "./openExternal";
import PdfInlineRenderer from "./PdfInlineRenderer";
import { getActiveProfileToken } from "../../utils/profileStorage";
import "./ActaViewer.css";

const getToken = () => getActiveProfileToken();

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
         (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};
const isAndroid = () =>
  typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
const isMobile = () => isIOS() || isAndroid();

const ActaViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [acta, setActa] = useState(location.state?.acta || null);
  const [busy, setBusy] = useState(false);

  useLayoutEffect(() => {
    document.body.classList.add("app-no-scroll");
    return () => document.body.classList.remove("app-no-scroll");
  }, []);

  useEffect(() => {
    if (acta) return;
    (async () => {
      const { data } = await GetPDFs();
      const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      const found = arr.find((p) => String(p.Id) === String(id));
      if (!found) return navigate("/descargas");
      setActa(found);
    })();
  }, [id, acta, navigate]);

  // ⬇️ Hooks *antes* de cualquier return condicional
  const token = getToken();
  const fileUrl = acta?.fileUrl || "";
  const downloadUrl = acta?.downloadUrl || acta?.fileUrl || "";

  const fileUrlWithToken = useMemo(
    () =>
      fileUrl
        ? `${fileUrl}${fileUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`
        : "",
    [fileUrl, token]
  );

  const downloadUrlWithToken = useMemo(
    () =>
      downloadUrl
        ? `${downloadUrl}${downloadUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`
        : "",
    [downloadUrl, token]
  );

  // Ahora sí, el return condicional
  if (!acta) {
    return (
      <div className="wv">
        <div className="wv-head">
          <button className="wv-back" onClick={() => navigate(-1)} aria-label="Volver">‹</button>
          <div className="wv-title"><div className="wv-name">Acta</div></div>
          <button className="wv-ok" onClick={() => navigate(-1)}>OK</button>
        </div>
        <div className="wv-msg">Cargando…</div>
      </div>
    );
  }

  const handleDownload = () => {
    if (!downloadUrlWithToken) return;
    if (isMobile()) return openExternal(downloadUrlWithToken);
    const a = document.createElement("a");
    a.href = downloadUrlWithToken;
    a.download = acta.fileName || "documento.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openInBrowser = async () => {
    if (!fileUrlWithToken) return;
    setBusy(true);
    await openExternal(fileUrlWithToken);
    setBusy(false);
  };

  return (
    <div className="wv">
      <div className="wv-head">
        <button className="wv-back" onClick={() => navigate(-1)} aria-label="Volver">‹</button>
        <div className="wv-title">
          <div className="wv-name" title={acta?.title || "Acta"}>
            {acta?.title || "Acta"}
          </div>
          {acta?.createdAt && (
            <div className="wv-sub">{new Date(acta.createdAt).toLocaleDateString()}</div>
          )}
        </div>
        <button className="wv-ok" onClick={() => navigate(-1)}>OK</button>
      </div>

      <div className="wv-toolbar">
        <div className="wv-tools-left">
          <span className="wv-zoom">Visor interno</span>
        </div>
        <div className="wv-tools-right">
          <button disabled={busy} onClick={handleDownload}>Descargar</button>
          <button disabled={busy} onClick={openInBrowser}>Abrir en navegador</button>
        </div>
      </div>

      <div className="wv-canvas">
        {isMobile() ? (
          <PdfInlineRenderer src={fileUrlWithToken} />
        ) : (
          <embed
            key={fileUrlWithToken}
            src={fileUrlWithToken}
            type="application/pdf"
            className="pdf-embed"
          />
        )}
      </div>
    </div>
  );
};

export default ActaViewer;
